import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { recibosCobroAPI, recibosCobroGrupoAPI, clientesAPI, usuariosAPI, cajasAPI } from '../services/api';
import { toast } from 'react-toastify';
import { SelectorColumnas } from '../components/SelectorColumnas';
import { SelectorFiltros } from '../components/SelectorFiltros';
import { useZonaHoraria } from '../context/ZonaHorariaContext';
import { useBranding } from '../context/BrandingContext';
import { FormasPagoDetalleList } from '../components/FormasPagoDetalleList';
import { cargarFormasRecibo, cargarFormasPago } from '../utils/cargarFormasComprobante';
import { imprimirListadoCarteraCheques, escHtml, valorCeldaCarteraExport } from '../utils/impresionCheques';
import { ExportActions } from '../components/ExportActions';
import { exportFromColumnas } from '../utils/exportListado';
import { fechaHoySistema } from '../utils/fechasSistema';
import { loadFiltrosSession, saveFiltrosSession } from '../utils/filtrosSession';
import '../MovimientosFondos.css';

const MESES_ES = ['Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio', 'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'];

const CLAVE_PREFERENCIAS = 'cartera_cheques_listado';
const CLAVE_FILTROS = 'cartera_cheques_listado_filtros';
const COLUMNAS_DEFINICION = [
  { id: 'cliente', label: 'Cliente' },
  { id: 'numero', label: 'Nº cheque' },
  { id: 'banco', label: 'Banco' },
  { id: 'caja', label: 'Caja actual' },
  { id: 'f_emision', label: 'F. emisión' },
  { id: 'f_cobro', label: 'F. cobro' },
  { id: 'importe', label: 'Importe' },
  { id: 'electronico', label: 'Electrónico' },
  { id: 'estado', label: 'Estado' },
  { id: 'comprobantes', label: 'Comprobantes' },
];
const FILTROS_DEFINICION = [
  { id: 'estado', label: 'Estado' },
  { id: 'cliente', label: 'Cliente' },
  { id: 'caja', label: 'Caja actual' },
  { id: 'numero', label: 'Nº cheque' },
  { id: 'importe', label: 'Importe mín.' },
  { id: 'f_emision', label: 'F. emisión' },
  { id: 'f_cobro', label: 'F. cobro' },
];
const ESTADOS_CHEQUE = [
  { value: 'recibido', label: 'Recibido (en cartera)' },
  { value: 'depositado', label: 'Depositado' },
  { value: 'acreditado', label: 'Acreditado' },
  { value: 'rechazado', label: 'Rechazado' },
  { value: 'entregado', label: 'Entregado a proveedor' },
  { value: 'vencido', label: 'Vencido' },
  { value: 'devuelto', label: 'Devuelto' },
];
const defaultColumnasVisibles = COLUMNAS_DEFINICION.reduce((acc, c) => ({ ...acc, [c.id]: true }), {});
const defaultFiltrosVisibles = FILTROS_DEFINICION.reduce((acc, f) => ({ ...acc, [f.id]: true }), {});
const defaultOrdenColumnas = COLUMNAS_DEFINICION.map(c => c.id);
const defaultFiltrosValores = {
  estado: '',
  cliente_id: '',
  numero: '',
  importe_min: '',
  fecha_emision_desde: '',
  fecha_emision_hasta: '',
  fecha_cobro_desde: '',
  fecha_cobro_hasta: '',
  caja_id: '',
};

const CarteraCheques = () => {
  const { formatFecha } = useZonaHoraria();
  const { logoUrl } = useBranding();
  const [searchParams] = useSearchParams();
  const cajaIdParam = searchParams.get('caja_id');
  const estadoParam = searchParams.get('estado');
  const [cheques, setCheques] = useState([]);
  const [clientes, setClientes] = useState([]);
  const [cajas, setCajas] = useState([]);
  const [loading, setLoading] = useState(true);
  const primeraCargaCarteraRef = useRef(true);
  const [refrescandoCartera, setRefrescandoCartera] = useState(false);
  const cargaCarteraSeqRef = useRef(0);
  const [detalleCheque, setDetalleCheque] = useState(null);
  const [detalleFormas, setDetalleFormas] = useState({ ingreso: [], egreso: [], loading: false });
  const [accionModal, setAccionModal] = useState(null); // 'rechazar' | 'reintegrar'
  const [clientesGrupoReintegro, setClientesGrupoReintegro] = useState(null);
  const [accionSubmitting, setAccionSubmitting] = useState(false);
  const [accionForm, setAccionForm] = useState({
    fecha: '',
    gastos_monto: '',
    librador_es_cliente: '',
    titular_override: '',
  });
  const [filtrosValores, setFiltrosValores] = useState(() => {
    const base = loadFiltrosSession(CLAVE_FILTROS, defaultFiltrosValores);
    try {
      const sp = new URLSearchParams(window.location.search);
      const caja = sp.get('caja_id');
      const estado = sp.get('estado');
      if (caja) base.caja_id = caja;
      if (estado) base.estado = estado;
    } catch (_) { /* ignore */ }
    return base;
  });
  const [columnasVisibles, setColumnasVisibles] = useState(() => ({ ...defaultColumnasVisibles }));
  const [filtrosVisibles, setFiltrosVisibles] = useState(() => ({ ...defaultFiltrosVisibles }));
  const [ordenColumnas, setOrdenColumnas] = useState(() => [...defaultOrdenColumnas]);
  const [sortByColumn, setSortByColumn] = useState(null);
  const [sortAsc, setSortAsc] = useState(true);

  useEffect(() => {
    saveFiltrosSession(CLAVE_FILTROS, filtrosValores);
  }, [filtrosValores]);

  // Deep link: si llega caja/estado por URL tras el mount, aplicar una vez
  useEffect(() => {
    if (!cajaIdParam && !estadoParam) return;
    setFiltrosValores((prev) => {
      const next = { ...prev };
      let changed = false;
      if (cajaIdParam && String(prev.caja_id) !== String(cajaIdParam)) {
        next.caja_id = cajaIdParam;
        changed = true;
      }
      if (estadoParam && String(prev.estado) !== String(estadoParam)) {
        next.estado = estadoParam;
        changed = true;
      }
      return changed ? next : prev;
    });
  }, [cajaIdParam, estadoParam]);
  const [, setLoadingPreferencias] = useState(true);
  const [savingPreferencias, setSavingPreferencias] = useState(false);

  const colVisible = (id) => columnasVisibles[id] !== false;
  const getSortValueCartera = (ch, colId) => {
    switch (colId) {
      case 'cliente': return (ch.cliente_razon_social || '').toString().toLowerCase();
      case 'numero': return (ch.numero || '').toString().toLowerCase();
      case 'banco': return (ch.cuenta_banco_nombre || '').toString().toLowerCase();
      case 'caja': return (ch.caja_ubicacion_nombre || '').toString().toLowerCase();
      case 'f_emision': return new Date(ch.fecha_emision || 0).getTime() || 0;
      case 'f_cobro': return new Date(ch.fecha_cobro || 0).getTime() || 0;
      case 'importe': return parseFloat(ch.importe || 0) || 0;
      case 'electronico': return ch.es_electronico ? 1 : 0;
      case 'estado': return (ch.estado || '').toString().toLowerCase();
      case 'comprobantes': return 0;
      default: return '';
    }
  };
  const filtroVisible = useCallback((filtroId) => filtrosVisibles[filtroId] !== false, [filtrosVisibles]);
  const setFiltro = (key, value) => setFiltrosValores(prev => ({ ...prev, [key]: value }));

  const loadClientes = useCallback(async () => {
    try {
      const res = await clientesAPI.getAll({ activo: true });
      if (res.success && Array.isArray(res.data)) setClientes(res.data);
      else setClientes([]);
    } catch (e) {
      setClientes([]);
    }
  }, []);

  const loadCajas = useCallback(async () => {
    try {
      const res = await cajasAPI.getAll({ activo: true });
      if (res.success && Array.isArray(res.data)) setCajas(res.data);
      else setCajas([]);
    } catch (e) {
      setCajas([]);
    }
  }, []);

  const loadCartera = useCallback(async () => {
    const mostrarLoaderPantalla = primeraCargaCarteraRef.current;
    const seq = ++cargaCarteraSeqRef.current;
    try {
      if (mostrarLoaderPantalla) setLoading(true);
      else setRefrescandoCartera(true);
      const params = {};
      if (filtroVisible('estado') && filtrosValores.estado) params.estado = filtrosValores.estado;
      if (filtroVisible('cliente') && filtrosValores.cliente_id) params.cliente_id = filtrosValores.cliente_id;
      if (filtroVisible('f_emision') && filtrosValores.fecha_emision_desde?.trim()) params.fecha_emision_desde = filtrosValores.fecha_emision_desde.trim();
      if (filtroVisible('f_emision') && filtrosValores.fecha_emision_hasta?.trim()) params.fecha_emision_hasta = filtrosValores.fecha_emision_hasta.trim();
      if (filtroVisible('f_cobro') && filtrosValores.fecha_cobro_desde?.trim()) params.fecha_cobro_desde = filtrosValores.fecha_cobro_desde.trim();
      if (filtroVisible('f_cobro') && filtrosValores.fecha_cobro_hasta?.trim()) params.fecha_cobro_hasta = filtrosValores.fecha_cobro_hasta.trim();
      if (filtroVisible('caja') && filtrosValores.caja_id) params.caja_id = filtrosValores.caja_id;
      const res = await recibosCobroAPI.getCarteraCheques(params);
      if (seq !== cargaCarteraSeqRef.current) return;
      if (res.success && Array.isArray(res.data)) {
        let data = res.data;
        if (filtroVisible('numero') && filtrosValores.numero?.trim()) {
          const busqueda = String(filtrosValores.numero).trim().toLowerCase();
          data = data.filter(ch => String(ch.numero || '').toLowerCase().includes(busqueda));
        }
        if (filtroVisible('importe') && filtrosValores.importe_min !== '') {
          const min = parseFloat(filtrosValores.importe_min);
          if (!isNaN(min)) data = data.filter(ch => parseFloat(ch.importe || 0) >= min);
        }
        setCheques(data);
      } else {
        setCheques([]);
      }
    } catch (error) {
      if (seq !== cargaCarteraSeqRef.current) return;
      toast.error(error.message || 'Error al cargar cartera de cheques');
      setCheques([]);
    } finally {
      if (mostrarLoaderPantalla) {
        setLoading(false);
        primeraCargaCarteraRef.current = false;
      }
      if (seq === cargaCarteraSeqRef.current) {
        setRefrescandoCartera(false);
      }
    }
  }, [filtrosValores, filtroVisible]);

  useEffect(() => {
    loadClientes();
    loadCajas();
  }, [loadClientes, loadCajas]);

  const esChequeGrupo = Boolean(detalleCheque?.recibo_cobro_grupo_id);

  const abrirAccion = async (tipo) => {
    if (!detalleCheque) return;
    const base = {
      fecha: fechaHoySistema(),
      gastos_monto: '',
      librador_es_cliente: '',
      titular_override: detalleCheque.titular || '',
    };
    setClientesGrupoReintegro(null);
    if (tipo === 'reintegrar') {
      try {
        const g = await recibosCobroAPI.getGastosRechazoCheque(detalleCheque.id);
        if (g.success && g.data?.total > 0) {
          base.gastos_monto = String(g.data.total);
        }
      } catch (_) { /* ignore */ }
      if (detalleCheque.recibo_cobro_grupo_id) {
        try {
          const rg = await recibosCobroGrupoAPI.getById(detalleCheque.recibo_cobro_grupo_id);
          const clientes = (rg?.data?.clientes || rg?.clientes || []).filter(
            (c) => (parseFloat(c.monto_asignado) || 0) > 0
          );
          setClientesGrupoReintegro(clientes.length || null);
        } catch (_) { /* ignore */ }
      }
    }
    setAccionForm(base);
    setAccionModal(tipo);
  };

  const confirmarAccion = async () => {
    if (!detalleCheque || !accionModal) return;
    setAccionSubmitting(true);
    try {
      if (accionModal === 'rechazar') {
        const body = {
          fecha: accionForm.fecha || undefined,
          gastos_monto: accionForm.gastos_monto !== '' ? parseFloat(accionForm.gastos_monto) : 0,
        };
        await recibosCobroAPI.marcarRechazadoCartera(detalleCheque.id, body);
        toast.success('Cheque marcado como rechazado');
      } else {
        const body = {
          fecha: accionForm.fecha || undefined,
          gastos_monto: accionForm.gastos_monto !== '' ? parseFloat(accionForm.gastos_monto) : undefined,
          librador_es_cliente:
            accionForm.librador_es_cliente === ''
              ? null
              : accionForm.librador_es_cliente === '1',
          titular_override: accionForm.titular_override || undefined,
        };
        const res = await recibosCobroAPI.reintegrarChequeCartera(detalleCheque.id, body);
        const n = res?.clientes_afectados || res?.movimientos?.length || 1;
        if (res?.es_grupo || detalleCheque.recibo_cobro_grupo_id) {
          toast.success(`Cheque reintegrado: ${n} DEBITO(s) en CC de ${n} cliente(s) del grupo`);
        } else {
          toast.success('Cheque reintegrado al cliente (movimientos en CC)');
        }
      }
      setAccionModal(null);
      setClientesGrupoReintegro(null);
      setDetalleCheque(null);
      loadCartera();
    } catch (e) {
      toast.error(e.message || 'No se pudo completar la acción');
    } finally {
      setAccionSubmitting(false);
    }
  };

  useEffect(() => {
    loadCartera();
  }, [loadCartera]);

  useEffect(() => {
    if (!detalleCheque) {
      setDetalleFormas({ ingreso: [], egreso: [], loading: false });
      return;
    }
    let cancelled = false;
    (async () => {
      setDetalleFormas({ ingreso: [], egreso: [], loading: true });
      const [ingreso, egreso] = await Promise.all([
        cargarFormasRecibo(detalleCheque.comprobante_ingreso),
        cargarFormasPago(detalleCheque.comprobante_egreso),
      ]);
      if (!cancelled) setDetalleFormas({ ingreso, egreso, loading: false });
    })();
    return () => { cancelled = true; };
  }, [detalleCheque]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoadingPreferencias(true);
      try {
        const res = await usuariosAPI.getPreferencias(CLAVE_PREFERENCIAS);
        if (cancelled) return;
        const raw = Array.isArray(res.data) ? res.data[0] : res.data;
        const data = raw?.valor ?? null;
        if (data && typeof data === 'object') {
          if (data.columnasVisibles && typeof data.columnasVisibles === 'object') {
            setColumnasVisibles(prev => ({ ...defaultColumnasVisibles, ...data.columnasVisibles }));
          }
          if (data.filtrosVisibles && typeof data.filtrosVisibles === 'object') {
            setFiltrosVisibles(prev => ({ ...defaultFiltrosVisibles, ...data.filtrosVisibles }));
          }
          if (data.ordenColumnas && Array.isArray(data.ordenColumnas) && data.ordenColumnas.length) {
            const saved = data.ordenColumnas.filter(id => defaultOrdenColumnas.includes(id));
            const nuevos = defaultOrdenColumnas.filter(id => !saved.includes(id));
            setOrdenColumnas([...saved, ...nuevos]);
          }
        }
      } catch (e) {
        if (!cancelled) console.error('Error al cargar preferencias:', e);
      } finally {
        if (!cancelled) setLoadingPreferencias(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const guardarPreferenciasListado = useCallback(async () => {
    setSavingPreferencias(true);
    try {
      await usuariosAPI.savePreferencias(CLAVE_PREFERENCIAS, {
        columnasVisibles: { ...columnasVisibles },
        filtrosVisibles: { ...filtrosVisibles },
        ordenColumnas: [...ordenColumnas],
      });
      toast.success('Configuración guardada. Se aplica solo para su usuario.');
    } catch (e) {
      toast.error(e.message || 'Error al guardar configuración');
    } finally {
      setSavingPreferencias(false);
    }
  }, [columnasVisibles, filtrosVisibles, ordenColumnas]);

  const ordenColumnasVisible = useMemo(
    () => ordenColumnas.filter(id => colVisible(id)),
    [ordenColumnas, columnasVisibles]
  );

  const chequesOrdenados = useMemo(() => {
    if (!sortByColumn) return cheques;
    const arr = [...cheques];
    arr.sort((a, b) => {
      const v1 = getSortValueCartera(a, sortByColumn);
      const v2 = getSortValueCartera(b, sortByColumn);
      const num = typeof v1 === 'number' && typeof v2 === 'number';
      const cmp = num ? v1 - v2 : String(v1).localeCompare(String(v2), 'es');
      return sortAsc ? cmp : -cmp;
    });
    return arr;
  }, [cheques, sortByColumn, sortAsc]);

  const handleSortColumn = (colId) => {
    if (sortByColumn === colId) setSortAsc(prev => !prev);
    else { setSortByColumn(colId); setSortAsc(true); }
  };

  const getColumnLabel = (colId) => COLUMNAS_DEFINICION.find(c => c.id === colId)?.label || colId;

  const etiquetaEstadoCheque = (estado) => {
    const found = ESTADOS_CHEQUE.find((e) => e.value === (estado || '').toLowerCase());
    return found?.label || (estado || 'recibido');
  };

  const claseBadgeEstado = (estado) => {
    const e = (estado || 'recibido').toLowerCase();
    if (e === 'depositado') return 'estado-depositado';
    if (e === 'acreditado') return 'estado-acreditado';
    if (e === 'rechazado') return 'estado-rechazado';
    if (e === 'entregado') return 'estado-entregado';
    if (e === 'vencido') return 'estado-vencido';
    if (e === 'devuelto') return 'estado-devuelto';
    return 'estado-recibido';
  };

  const cajaFiltroNombre = useMemo(() => {
    if (!filtrosValores.caja_id) return null;
    const c = cajas.find((x) => String(x.id) === String(filtrosValores.caja_id));
    return c?.nombre || null;
  }, [cajas, filtrosValores.caja_id]);

  const generarPDF = () => {
    if (!chequesOrdenados.length) {
      toast.info('No hay cheques para imprimir con los filtros actuales');
      return;
    }
    const fechaReporte = new Date();
    const fechaReporteStr = `${fechaReporte.getDate()} de ${MESES_ES[fechaReporte.getMonth()]} de ${fechaReporte.getFullYear()}`;
    const partesFiltro = [];
    if (filtrosValores.estado) {
      const est = ESTADOS_CHEQUE.find((e) => e.value === filtrosValores.estado);
      partesFiltro.push(`<p><strong>Estado:</strong> ${escHtml(est?.label || filtrosValores.estado)}</p>`);
    }
    if (filtrosValores.cliente_id) {
      const cli = clientes.find((c) => String(c.id) === String(filtrosValores.cliente_id));
      partesFiltro.push(`<p><strong>Cliente:</strong> ${escHtml(cli?.razon_social || filtrosValores.cliente_id)}</p>`);
    }
    if (filtrosValores.caja_id) {
      partesFiltro.push(`<p><strong>Caja:</strong> ${escHtml(cajaFiltroNombre || filtrosValores.caja_id)}</p>`);
    }
    if (filtrosValores.numero?.trim()) partesFiltro.push(`<p><strong>Nº cheque:</strong> ${escHtml(filtrosValores.numero.trim())}</p>`);
    if (filtrosValores.importe_min !== '') partesFiltro.push(`<p><strong>Importe mín.:</strong> $${parseFloat(filtrosValores.importe_min || 0).toLocaleString('es-AR', { minimumFractionDigits: 2 })}</p>`);
    if (filtrosValores.fecha_emision_desde || filtrosValores.fecha_emision_hasta) {
      partesFiltro.push(`<p><strong>F. emisión:</strong> ${filtrosValores.fecha_emision_desde || '...'} a ${filtrosValores.fecha_emision_hasta || '...'}</p>`);
    }
    if (filtrosValores.fecha_cobro_desde || filtrosValores.fecha_cobro_hasta) {
      partesFiltro.push(`<p><strong>F. cobro:</strong> ${filtrosValores.fecha_cobro_desde || '...'} a ${filtrosValores.fecha_cobro_hasta || '...'}</p>`);
    }
    try {
      imprimirListadoCarteraCheques({
        cheques: chequesOrdenados,
        columnasDefinicion: COLUMNAS_DEFINICION,
        columnasVisiblesIds: ordenColumnasVisible,
        formatFecha,
        filtrosHtml: partesFiltro.join(''),
        logoUrl,
        subtitulo: `Reporte generado el ${fechaReporteStr}`,
      });
    } catch (e) {
      toast.error(e.message || 'Error al imprimir');
    }
  };

  const handleExportListado = async (format) => {
    try {
      await exportFromColumnas({
        filename: 'cartera_cheques',
        sheetName: 'Cheques',
        columnDefs: COLUMNAS_DEFINICION,
        visibleColumnIds: ordenColumnasVisible,
        items: chequesOrdenados,
        getCellValue: (ch, colId) => valorCeldaCarteraExport(ch, colId, formatFecha),
        format,
      });
    } catch (e) {
      toast.error(e.message || 'Error al exportar');
    }
  };

  if (loading) {
    return <div className="loading">Cargando...</div>;
  }

  return (
    <div className="clientes cartera-cheques-page pagina-listado pagina-listado--scroll">
      <div className="page-header">
        <div className="page-header-titulo">
          <h1>Cartera de cheques</h1>
          <p className="page-subtitle">Cheques de terceros. Ingreso por recibo de cobro; egreso por orden de pago.</p>
          {cajaFiltroNombre && (
            <p className="page-subtitle" style={{ color: '#2980b9' }}>
              Filtrado por caja: <strong>{cajaFiltroNombre}</strong>
            </p>
          )}
        </div>
        {chequesOrdenados.length > 0 && (
          <div className="page-header-acciones">
            <ExportActions
              onCsv={() => handleExportListado('csv')}
              onExcel={() => handleExportListado('excel')}
              onPrint={generarPDF}
              printLabel="Imprimir listado"
            />
          </div>
        )}
      </div>

      <div className="listado-panel clientes-listado listado-controles">
      <div className="filters cartera-cheques-filtros">
            {filtroVisible('estado') && (
              <select value={filtrosValores.estado} onChange={(e) => setFiltro('estado', e.target.value)} title="Estado del cheque" className="filtro-estado-cheque">
                <option value="">Todos los estados</option>
                {ESTADOS_CHEQUE.map((e) => (
                  <option key={e.value} value={e.value}>{e.label}</option>
                ))}
              </select>
            )}
            {filtroVisible('cliente') && (
              <select value={filtrosValores.cliente_id} onChange={(e) => setFiltro('cliente_id', e.target.value)} style={{ minWidth: 200 }}>
                <option value="">Todos los clientes</option>
                {clientes.map(c => (
                  <option key={c.id} value={c.id}>{c.razon_social}</option>
                ))}
              </select>
            )}
            {filtroVisible('caja') && (
              <select
                value={filtrosValores.caja_id}
                onChange={(e) => setFiltro('caja_id', e.target.value)}
                style={{ minWidth: 180 }}
                title="Caja donde está el cheque actualmente"
              >
                <option value="">Todas las cajas</option>
                {cajas.map((c) => (
                  <option key={c.id} value={c.id}>{c.nombre}</option>
                ))}
              </select>
            )}
            {filtroVisible('numero') && (
              <input
                type="text"
                placeholder="Nº cheque"
                value={filtrosValores.numero}
                onChange={(e) => setFiltro('numero', e.target.value)}
                style={{ width: 120, padding: '8px 10px' }}
              />
            )}
            {filtroVisible('importe') && (
              <input
                type="number"
                placeholder="Importe mín."
                value={filtrosValores.importe_min}
                onChange={(e) => setFiltro('importe_min', e.target.value)}
                step="0.01"
                min="0"
                style={{ width: 120, padding: '8px 10px' }}
              />
            )}
            {filtroVisible('f_emision') && (
              <>
                <input
                  type="date"
                  placeholder="F. emisión desde"
                  value={filtrosValores.fecha_emision_desde}
                  onChange={(e) => setFiltro('fecha_emision_desde', e.target.value)}
                  style={{ width: 140, padding: '8px 10px' }}
                  title="Fecha emisión desde"
                />
                <input
                  type="date"
                  placeholder="F. emisión hasta"
                  value={filtrosValores.fecha_emision_hasta}
                  onChange={(e) => setFiltro('fecha_emision_hasta', e.target.value)}
                  style={{ width: 140, padding: '8px 10px' }}
                  title="Fecha emisión hasta"
                />
              </>
            )}
            {filtroVisible('f_cobro') && (
              <>
                <input
                  type="date"
                  placeholder="F. cobro desde"
                  value={filtrosValores.fecha_cobro_desde}
                  onChange={(e) => setFiltro('fecha_cobro_desde', e.target.value)}
                  style={{ width: 140, padding: '8px 10px' }}
                  title="Fecha cobro desde"
                />
                <input
                  type="date"
                  placeholder="F. cobro hasta"
                  value={filtrosValores.fecha_cobro_hasta}
                  onChange={(e) => setFiltro('fecha_cobro_hasta', e.target.value)}
                  style={{ width: 140, padding: '8px 10px' }}
                  title="Fecha cobro hasta"
                />
              </>
            )}
            <button type="button" className="btn-action-icon" onClick={() => setFiltrosValores({ ...defaultFiltrosValores })} title="Limpiar todos los filtros">
              <span aria-hidden="true">🧹</span>
            </button>
      </div>
      <div className="filters-toolbar filters-toolbar--acciones">
            <SelectorColumnas
              columnas={COLUMNAS_DEFINICION}
              visibleIds={ordenColumnas.filter(id => columnasVisibles[id] !== false)}
              onToggle={(id) => setColumnasVisibles(prev => ({ ...prev, [id]: !prev[id] }))}
              onRestaurar={() => setColumnasVisibles({ ...defaultColumnasVisibles })}
              onSave={guardarPreferenciasListado}
              ordenColumnas={ordenColumnas}
              onOrdenChange={setOrdenColumnas}
              titulo="Columnas visibles"
              labelBoton="Columnas"
            />
            <SelectorFiltros
              filtros={FILTROS_DEFINICION}
              visibleIds={Object.keys(filtrosVisibles).filter(id => filtrosVisibles[id] !== false)}
              onToggle={(id) => setFiltrosVisibles(prev => ({ ...prev, [id]: !prev[id] }))}
              onRestaurar={() => setFiltrosVisibles({ ...defaultFiltrosVisibles })}
              onSave={guardarPreferenciasListado}
              titulo="Filtros visibles"
              labelBoton="Filtros"
            />
      </div>

      <div className="listado-panel-cuerpo clientes-listado-cuerpo">
      <div className={`listado-tabla-area clientes-listado-tabla-area${refrescandoCartera ? ' listado-tabla-area--refreshing clientes-listado-tabla-area--refreshing' : ''}`}>
          <div className="table-container">
            <table className="data-table">
              <thead>
                <tr>
                  {ordenColumnasVisible.map(colId => (
                    <th
                      key={colId}
                      className="th-sortable"
                      onClick={() => handleSortColumn(colId)}
                      title={`Ordenar por ${getColumnLabel(colId)}`}
                    >
                      <span>{getColumnLabel(colId)}</span>
                      {sortByColumn === colId && (
                        <span className="sort-indicator" aria-hidden="true">
                          {sortAsc ? ' ▲' : ' ▼'}
                        </span>
                      )}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {chequesOrdenados.length === 0 ? (
                  <tr>
                    <td colSpan={ordenColumnasVisible.length || 1} className="no-data">
                      No hay cheques de terceros que coincidan con el filtro
                    </td>
                  </tr>
                ) : (
                  chequesOrdenados.map((ch) => (
                    <tr key={ch.id}>
                      {ordenColumnasVisible.map(colId => {
                        if (colId === 'cliente') return <td key={colId}>{ch.cliente_razon_social || '-'}</td>;
                        if (colId === 'numero') return <td key={colId}>{ch.numero || '-'}</td>;
                        if (colId === 'banco') return <td key={colId}>{ch.cuenta_banco_nombre || '-'}</td>;
                        if (colId === 'caja') return <td key={colId}>{ch.caja_ubicacion_nombre || '—'}</td>;
                        if (colId === 'f_emision') return <td key={colId}>{formatFecha(ch.fecha_emision)}</td>;
                        if (colId === 'f_cobro') return <td key={colId}>{formatFecha(ch.fecha_cobro)}</td>;
                        if (colId === 'importe') return <td key={colId} style={{ textAlign: 'right' }}>${parseFloat(ch.importe || 0).toLocaleString('es-AR', { minimumFractionDigits: 2 })}</td>;
                        if (colId === 'electronico') return <td key={colId}>{ch.es_electronico ? 'Sí' : 'No'}</td>;
                        if (colId === 'estado') return (
                          <td key={colId}>
                            <span className={`badge estado-cheque ${claseBadgeEstado(ch.estado)}`}>
                              {etiquetaEstadoCheque(ch.estado)}
                            </span>
                          </td>
                        );
                        if (colId === 'comprobantes') return (
                          <td key={colId}>
                            <button type="button" className="btn-secondary" style={{ padding: '6px 10px', fontSize: '12px' }} onClick={() => setDetalleCheque(ch)}>
                              Ver detalle
                            </button>
                          </td>
                        );
                        return <td key={colId}>-</td>;
                      })}
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
      {refrescandoCartera && (
        <div className="listado-refresh-overlay clientes-listado-refresh-overlay" role="status" aria-live="polite">
          <span className="clientes-listado-refresh-spinner" aria-hidden="true" />
          <span className="clientes-listado-refresh-label">Actualizando…</span>
        </div>
      )}
      </div>
      </div>
      </div>

          {detalleCheque && (
            <div
              style={{
                position: 'fixed',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                background: 'rgba(0,0,0,0.5)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                zIndex: 1000,
              }}
              onClick={() => setDetalleCheque(null)}
            >
              <div
                style={{
                  background: 'white',
                  borderRadius: '8px',
                  padding: '20px',
                  maxWidth: '480px',
                  width: '95%',
                  boxShadow: '0 4px 20px rgba(0,0,0,0.2)',
                }}
                onClick={(e) => e.stopPropagation()}
              >
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
                  <h3 style={{ margin: 0 }}>Comprobantes del cheque</h3>
                  <button type="button" onClick={() => setDetalleCheque(null)} style={{ background: 'none', border: 'none', fontSize: '22px', cursor: 'pointer', lineHeight: 1 }}>×</button>
                </div>
                <p style={{ fontSize: '13px', color: '#555', marginBottom: '12px' }}>
                  Nº {detalleCheque.numero} · ${parseFloat(detalleCheque.importe || 0).toLocaleString('es-AR', { minimumFractionDigits: 2 })} · {detalleCheque.cliente_razon_social || '-'}
                </p>
                <div style={{ borderTop: '1px solid #eee', paddingTop: '12px' }}>
                  <div style={{ marginBottom: '14px' }}>
                    <strong style={{ display: 'block', marginBottom: '4px', color: '#27ae60' }}>Ingreso (recibido)</strong>
                    {detalleCheque.comprobante_ingreso && (
                      <div style={{ fontSize: '14px', padding: '10px', background: '#f8fff8', borderRadius: '6px' }}>
                        <div><strong>{detalleCheque.comprobante_ingreso.tipo}</strong></div>
                        {(detalleCheque.comprobante_ingreso.cliente_razon_social || detalleCheque.cliente_razon_social) && (
                          <div style={{ color: '#333', marginTop: '4px' }}><strong>Cliente:</strong> {detalleCheque.comprobante_ingreso.cliente_razon_social || detalleCheque.cliente_razon_social}</div>
                        )}
                        <div>{detalleCheque.comprobante_ingreso.concepto || '-'}</div>
                        <div style={{ color: '#666', marginTop: '4px' }}>Fecha: {formatFecha(detalleCheque.comprobante_ingreso.fecha)}</div>
                        {detalleCheque.comprobante_ingreso.numero_recibo && (
                          <div style={{ color: '#666' }}>N° recibo: {detalleCheque.comprobante_ingreso.numero_recibo}</div>
                        )}
                        <FormasPagoDetalleList
                          formasPago={detalleFormas.ingreso}
                          titulo="Formas de pago del recibo"
                          loading={detalleFormas.loading}
                          formatFecha={formatFecha}
                        />
                      </div>
                    )}
                  </div>
                  <div style={{ marginBottom: '14px' }}>
                    <strong style={{ display: 'block', marginBottom: '4px', color: '#2980b9' }}>Depósito en cuenta</strong>
                    {detalleCheque.comprobante_deposito ? (
                      <div style={{ fontSize: '14px', padding: '10px', background: '#f0f7ff', borderRadius: '6px' }}>
                        <div><strong>{detalleCheque.comprobante_deposito.tipo}</strong></div>
                        <div>{detalleCheque.comprobante_deposito.concepto || '-'}</div>
                        <div style={{ color: '#666', marginTop: '4px' }}>Fecha: {formatFecha(detalleCheque.comprobante_deposito.fecha)}</div>
                        {(detalleCheque.comprobante_deposito.cuenta_numero || detalleCheque.comprobante_deposito.banco_nombre) && (
                          <div style={{ color: '#666' }}>
                            Cuenta: {detalleCheque.comprobante_deposito.cuenta_numero || '-'}
                            {detalleCheque.comprobante_deposito.banco_nombre ? ` — ${detalleCheque.comprobante_deposito.banco_nombre}` : ''}
                          </div>
                        )}
                        {detalleCheque.comprobante_deposito.movimiento_fondo_id && (
                          <div style={{ color: '#666' }}>Mov. fondos #{detalleCheque.comprobante_deposito.movimiento_fondo_id}</div>
                        )}
                      </div>
                    ) : detalleCheque.estado === 'depositado' ? (
                      <div style={{ fontSize: '14px', color: '#888', fontStyle: 'italic' }}>Depositado (sin vínculo de movimiento en sistema)</div>
                    ) : detalleCheque.estado === 'acreditado' ? (
                      <div style={{ fontSize: '14px', color: '#888', fontStyle: 'italic' }}>Acreditado en banco</div>
                    ) : detalleCheque.estado === 'rechazado' ? (
                      <div style={{ fontSize: '14px', color: '#888', fontStyle: 'italic' }}>Rechazado por el banco</div>
                    ) : (
                      <div style={{ fontSize: '14px', color: '#888', fontStyle: 'italic' }}>Sin depósito registrado</div>
                    )}
                  </div>
                  <div>
                    <strong style={{ display: 'block', marginBottom: '4px', color: '#c0392b' }}>Egreso (pago / entrega)</strong>
                    {detalleCheque.comprobante_egreso ? (
                      <div style={{ fontSize: '14px', padding: '10px', background: '#fff8f8', borderRadius: '6px' }}>
                        <div><strong>{detalleCheque.comprobante_egreso.tipo} N° {detalleCheque.comprobante_egreso.numero ?? detalleCheque.comprobante_egreso.numero_pago_cuenta ?? detalleCheque.comprobante_egreso.numero_pago}</strong></div>
                        {detalleCheque.comprobante_egreso.proveedor_razon_social && (
                          <div style={{ color: '#333', marginTop: '4px' }}><strong>Proveedor:</strong> {detalleCheque.comprobante_egreso.proveedor_razon_social}</div>
                        )}
                        {detalleCheque.comprobante_egreso.cliente_razon_social && (
                          <div style={{ color: '#333', marginTop: '4px' }}><strong>Cliente:</strong> {detalleCheque.comprobante_egreso.cliente_razon_social}</div>
                        )}
                        <div>{detalleCheque.comprobante_egreso.concepto || '-'}</div>
                        <div style={{ color: '#666', marginTop: '4px' }}>Fecha: {formatFecha(detalleCheque.comprobante_egreso.fecha)}</div>
                        <FormasPagoDetalleList
                          formasPago={detalleFormas.egreso}
                          titulo={detalleCheque.comprobante_egreso.tipo === 'Pago a cuenta' ? 'Formas de pago del pago a cuenta' : 'Formas de pago de la orden'}
                          loading={detalleFormas.loading}
                          formatFecha={formatFecha}
                        />
                      </div>
                    ) : (
                      <div style={{ fontSize: '14px', color: '#888', fontStyle: 'italic' }}>
                        {['depositado', 'acreditado', 'rechazado'].includes(String(detalleCheque.estado || '').toLowerCase()) || detalleCheque.comprobante_deposito
                          ? `No entregado (${detalleCheque.estado || 'depositado'})`
                          : 'No entregado (en cartera)'}
                      </div>
                    )}
                  </div>
                </div>
                {['depositado', 'recibido', 'rechazado'].includes(String(detalleCheque.estado || '').toLowerCase()) && (
                  <div style={{ borderTop: '1px solid #eee', marginTop: 16, paddingTop: 12, display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                    {String(detalleCheque.estado).toLowerCase() === 'depositado' && (
                      <button type="button" className="btn btn-secondary btn-sm" onClick={() => abrirAccion('rechazar')}>
                        Marcar rechazado
                      </button>
                    )}
                    {['recibido', 'rechazado'].includes(String(detalleCheque.estado).toLowerCase()) && (
                      <button type="button" className="btn btn-primary btn-sm" onClick={() => abrirAccion('reintegrar')}>
                        {detalleCheque.recibo_cobro_grupo_id ? 'Reintegrar a clientes del grupo' : 'Reintegrar al cliente'}
                      </button>
                    )}
                  </div>
                )}
              </div>
            </div>
          )}

          {accionModal && detalleCheque && (
            <div
              style={{
                position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)',
                display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1100,
              }}
              onClick={() => !accionSubmitting && setAccionModal(null)}
            >
              <div
                style={{ background: '#fff', borderRadius: 8, padding: 20, maxWidth: 420, width: '95%' }}
                onClick={(e) => e.stopPropagation()}
              >
                <h3 style={{ marginTop: 0 }}>
                  {accionModal === 'rechazar'
                    ? 'Marcar cheque rechazado'
                    : esChequeGrupo
                      ? 'Reintegrar cheque a clientes del grupo'
                      : 'Reintegrar cheque al cliente'}
                </h3>
                <p style={{ fontSize: 13, color: '#555' }}>
                  Nº {detalleCheque.numero} · ${parseFloat(detalleCheque.importe || 0).toLocaleString('es-AR', { minimumFractionDigits: 2 })}
                  {detalleCheque.titular ? ` · Titular: ${detalleCheque.titular}` : ''}
                </p>
                <div className="form-group">
                  <label>Fecha</label>
                  <input
                    type="date"
                    value={accionForm.fecha}
                    onChange={(e) => setAccionForm((p) => ({ ...p, fecha: e.target.value }))}
                  />
                </div>
                {accionModal === 'rechazar' && (
                  <>
                    <div className="form-group">
                      <label>Cuenta del depósito</label>
                      <div style={{ fontSize: 14, padding: '8px 10px', background: '#f5f7fa', borderRadius: 6 }}>
                        {detalleCheque.comprobante_deposito?.banco_nombre || detalleCheque.comprobante_deposito?.cuenta_numero
                          ? [
                              detalleCheque.comprobante_deposito.banco_nombre,
                              detalleCheque.comprobante_deposito.cuenta_numero,
                            ].filter(Boolean).join(' — ')
                          : 'Se tomará automáticamente la cuenta donde se depositó el cheque'}
                      </div>
                    </div>
                    <div className="form-group">
                      <label>Gastos bancarios (opc.)</label>
                      <input
                        type="number"
                        step="0.01"
                        min="0"
                        value={accionForm.gastos_monto}
                        onChange={(e) => setAccionForm((p) => ({ ...p, gastos_monto: e.target.value }))}
                      />
                    </div>
                  </>
                )}
                {accionModal === 'reintegrar' && (
                  <>
                    <div className="form-group">
                      <label>{esChequeGrupo ? 'Gastos a debitar (reparto entre clientes)' : 'Gastos a debitar al cliente'}</label>
                      <input
                        type="number"
                        step="0.01"
                        min="0"
                        value={accionForm.gastos_monto}
                        onChange={(e) => setAccionForm((p) => ({ ...p, gastos_monto: e.target.value }))}
                        placeholder="0 = solo monto del cheque"
                      />
                    </div>
                    <div className="form-group">
                      <label>Librador</label>
                      <select
                        value={accionForm.librador_es_cliente}
                        onChange={(e) => setAccionForm((p) => ({ ...p, librador_es_cliente: e.target.value }))}
                      >
                        <option value="">Inferir automáticamente</option>
                        <option value="1">Propio del cliente</option>
                        <option value="0">De otro tercero</option>
                      </select>
                    </div>
                    {accionForm.librador_es_cliente === '0' && (
                      <div className="form-group">
                        <label>Nombre del librador</label>
                        <input
                          type="text"
                          value={accionForm.titular_override}
                          onChange={(e) => setAccionForm((p) => ({ ...p, titular_override: e.target.value }))}
                        />
                      </div>
                    )}
                    <p style={{ fontSize: 12, color: '#666' }}>
                      {esChequeGrupo ? (
                        <>
                          Se debitará a {clientesGrupoReintegro != null ? `${clientesGrupoReintegro} clientes` : 'los clientes'}
                          {' '}según el reparto del recibo de grupo
                          {parseFloat(accionForm.gastos_monto) > 0 ? ' (cheque y gastos)' : ' (importe del cheque)'}.
                          {String(detalleCheque.estado).toLowerCase() === 'recibido' ? ' También un egreso en la caja donde está el cheque.' : ''}
                        </>
                      ) : (
                        <>
                          Se generará un DEBITO por el cheque
                          {parseFloat(accionForm.gastos_monto) > 0 ? ' y otro por los gastos' : ''}
                          {' '}en la cuenta corriente del cliente.
                          {String(detalleCheque.estado).toLowerCase() === 'recibido' ? ' También un egreso en la caja donde está el cheque.' : ''}
                        </>
                      )}
                    </p>
                  </>
                )}
                <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 12 }}>
                  <button type="button" className="btn btn-secondary btn-sm" disabled={accionSubmitting} onClick={() => setAccionModal(null)}>
                    Cancelar
                  </button>
                  <button type="button" className="btn btn-primary btn-sm" disabled={accionSubmitting} onClick={confirmarAccion}>
                    {accionSubmitting ? 'Procesando…' : 'Confirmar'}
                  </button>
                </div>
              </div>
            </div>
          )}
    </div>
  );
};

export default CarteraCheques;
